import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WellnessPreferences = ({ user, onDataChange }) => {
  const [preferences, setPreferences] = useState({
    meditationStyles: user?.preferences?.meditationStyles || ['mindfulness', 'breathwork'],
    energyHealingModalities: user?.preferences?.energyHealingModalities || ['ees', 'reiki'],
    dietaryRequirements: user?.preferences?.dietaryRequirements || [],
    accessibilityNeeds: user?.preferences?.accessibilityNeeds || [],
    sessionDuration: user?.preferences?.sessionDuration || '60-90',
    preferredTimes: user?.preferences?.preferredTimes || ['morning'],
    notifications: user?.preferences?.notifications || {
      sessionReminders: true,
      wellnessTips: true,
      centerUpdates: false,
      communityEvents: true,
      progressUpdates: true
    },
    privacySettings: user?.preferences?.privacySettings || {
      showProgressPublicly: false,
      allowPractitionerContact: true,
      shareDataForResearch: false,
      anonymousTestimonials: true
    }
  });

  const handlePreferenceChange = (category, value) => {
    setPreferences(prev => ({ ...prev, [category]: value }));
    onDataChange?.();
  };

  const handleArrayToggle = (category, item) => {
    const current = preferences[category] || [];
    const updated = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    handlePreferenceChange(category, updated);
  };

  const handleNestedChange = (category, subcategory, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: value
      }
    }));
    onDataChange?.();
  };

  const meditationOptions = [
    { id: 'mindfulness', label: 'Mindfulness', icon: 'Brain' },
    { id: 'breathwork', label: 'Breathwork', icon: 'Wind' },
    { id: 'guided', label: 'Guided Visualization', icon: 'Eye' },
    { id: 'movement', label: 'Movement Meditation', icon: 'Activity' },
    { id: 'sound', label: 'Sound Healing', icon: 'Volume2' },
    { id: 'silent', label: 'Silent Meditation', icon: 'VolumeX' }
  ];

  const healingModalities = [
    { id: 'ees', label: 'EES (Energy Enhancement System)', icon: 'Zap' },
    { id: 'reiki', label: 'Reiki', icon: 'Hand' },
    { id: 'crystals', label: 'Crystal Therapy', icon: 'Gem' },
    { id: 'chakra', label: 'Chakra Balancing', icon: 'Circle' },
    { id: 'acupuncture', label: 'Acupuncture', icon: 'Target' },
    { id: 'massage', label: 'Therapeutic Massage', icon: 'Heart' }
  ];

  const dietaryOptions = [
    { id: 'vegetarian', label: 'Vegetarian' },
    { id: 'vegan', label: 'Vegan' },
    { id: 'gluten-free', label: 'Gluten-Free' },
    { id: 'dairy-free', label: 'Dairy-Free' },
    { id: 'keto', label: 'Ketogenic' },
    { id: 'organic', label: 'Organic Only' }
  ];

  const accessibilityOptions = [
    { id: 'wheelchair', label: 'Wheelchair Accessible' },
    { id: 'hearing', label: 'Hearing Assistance' },
    { id: 'visual', label: 'Visual Assistance' },
    { id: 'mobility', label: 'Mobility Support' },
    { id: 'cognitive', label: 'Cognitive Support' },
    { id: 'companion', label: 'Companion Access' }
  ];

  const timeOptions = [
    { id: 'early-morning', label: 'Early Morning (6-9 AM)' },
    { id: 'morning', label: 'Morning (9-12 PM)' },
    { id: 'afternoon', label: 'Afternoon (12-5 PM)' },
    { id: 'evening', label: 'Evening (5-8 PM)' },
    { id: 'late-evening', label: 'Late Evening (8+ PM)' }
  ];

  return (
    <div className="space-y-8">
      {/* Meditation & Practice Preferences */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="Brain" size={20} className="mr-2 text-blue-600" />
            Meditation & Practice Styles
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Choose your preferred meditation and mindfulness practices
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {meditationOptions.map((style) => (
              <button
                key={style.id}
                onClick={() => handleArrayToggle('meditationStyles', style.id)}
                className={`p-4 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                  preferences.meditationStyles?.includes(style.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-blue-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon name={style.icon} size={20} className={preferences.meditationStyles?.includes(style.id) ? 'text-blue-600' : 'text-slate-500'} />
                  <span className="text-center">{style.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Energy Healing Preferences */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="Zap" size={20} className="mr-2 text-blue-600" />
            Energy Healing Modalities
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Select your preferred energy healing and therapeutic approaches
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {healingModalities.map((modality) => (
              <button
                key={modality.id}
                onClick={() => handleArrayToggle('energyHealingModalities', modality.id)}
                className={`p-4 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                  preferences.energyHealingModalities?.includes(modality.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-blue-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon name={modality.icon} size={20} className={preferences.energyHealingModalities?.includes(modality.id) ? 'text-blue-600' : 'text-slate-500'} />
                  <span className="text-center">{modality.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Session & Accessibility Preferences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Session Preferences */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <div className="px-6 py-4 border-b border-blue-100">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
              <Icon name="Clock" size={18} className="mr-2 text-blue-600" />
              Session Preferences
            </h3>
          </div>
          
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Preferred Session Duration
              </label>
              <div className="space-y-2">
                {[
                  { value: '30-45', label: '30-45 minutes' },
                  { value: '60-90', label: '60-90 minutes' },
                  { value: '90-120', label: '90-120 minutes' },
                  { value: '120+', label: '2+ hours' }
                ].map((duration) => (
                  <button
                    key={duration.value}
                    onClick={() => handlePreferenceChange('sessionDuration', duration.value)}
                    className={`w-full text-left p-3 rounded-lg border-2 text-sm transition-all duration-200 ${
                      preferences.sessionDuration === duration.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-blue-200 bg-white text-slate-600 hover:border-blue-300'
                    }`}
                  >
                    {duration.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Preferred Times
              </label>
              <div className="space-y-2">
                {timeOptions.map((time) => (
                  <button
                    key={time.id}
                    onClick={() => handleArrayToggle('preferredTimes', time.id)}
                    className={`w-full text-left p-3 rounded-lg border-2 text-sm transition-all duration-200 ${
                      preferences.preferredTimes?.includes(time.id)
                        ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-blue-200 bg-white text-slate-600 hover:border-blue-300'
                    }`}
                  >
                    {time.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Dietary & Accessibility */}
        <div className="space-y-8">
          {/* Dietary Requirements */}
          <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
            <div className="px-6 py-4 border-b border-blue-100">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <Icon name="Utensils" size={18} className="mr-2 text-blue-600" />
                Dietary Requirements
              </h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 gap-2">
                {dietaryOptions.map((diet) => (
                  <label key={diet.id} className="flex items-center space-x-3 p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200">
                    <input
                      type="checkbox"
                      checked={preferences.dietaryRequirements?.includes(diet.id)}
                      onChange={() => handleArrayToggle('dietaryRequirements', diet.id)}
                      className="w-4 h-4 text-blue-600 border-blue-200 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">{diet.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Accessibility Needs */}
          <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
            <div className="px-6 py-4 border-b border-blue-100">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <Icon name="Accessibility" size={18} className="mr-2 text-blue-600" />
                Accessibility Needs
              </h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 gap-2">
                {accessibilityOptions.map((access) => (
                  <label key={access.id} className="flex items-center space-x-3 p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200">
                    <input
                      type="checkbox"
                      checked={preferences.accessibilityNeeds?.includes(access.id)}
                      onChange={() => handleArrayToggle('accessibilityNeeds', access.id)}
                      className="w-4 h-4 text-blue-600 border-blue-200 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">{access.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="Bell" size={20} className="mr-2 text-blue-600" />
            Notification Preferences
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Control how and when you receive wellness updates and reminders
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries({
              sessionReminders: 'Session Reminders',
              wellnessTips: 'Wellness Tips & Insights',
              centerUpdates: 'EES Center Updates',
              communityEvents: 'Community Events',
              progressUpdates: 'Progress & Achievement Updates'
            }).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors duration-200">
                <div className="flex items-center space-x-3">
                  <Icon name="Bell" size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                </div>
                <button
                  onClick={() => handleNestedChange('notifications', key, !preferences.notifications?.[key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    preferences.notifications?.[key] ? 'bg-blue-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                      preferences.notifications?.[key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessPreferences;